import { ZSidLabel } from "src/types";

import { getWorkflowStepDisplayName } from "./getWorkflowStepDisplayName";
import {
  zSystemStepName,
  ZSystemWorkflowStep,
  ZUserWorkflowStep,
  ZWorkflowStep,
  zWorkflowStepType,
} from "./ZWorkflowStep";

export const isUserStep = (step: ZWorkflowStep): step is ZUserWorkflowStep =>
  step.type === zWorkflowStepType.enum.user;

export const isSystemStep = (
  step: ZWorkflowStep,
): step is ZSystemWorkflowStep => step.type === zWorkflowStepType.enum.system;

export const isInitialStep = (step: ZWorkflowStep): boolean =>
  isSystemStep(step) && step.name === zSystemStepName.enum.INITIAL;

/**
 * У РП, созданных до появления системных шагов, Initial нет
 */
export const findInitialStep = (
  steps: ZWorkflowStep[],
): ZSystemWorkflowStep | undefined =>
  steps.filter(isSystemStep).find(isInitialStep);

const hasPredecessor = (step: ZWorkflowStep, predecessorId: string) =>
  !!step.predecessors?.some(({ id }) => id === predecessorId);

/**
 * Первый пользовательский шаг - единственный пользовательский шаг,
 * у которого в предшественниках указан системный шаг Initial
 */
export const findFirstUserStep = (
  steps: ZWorkflowStep[],
): ZUserWorkflowStep | undefined => {
  const initial = findInitialStep(steps);
  if (!initial) return undefined;
  return steps
    .filter(isUserStep)
    .find((step) => hasPredecessor(step, initial.id));
};

export type PredecessorOption = {
  step: ZWorkflowStep;
  disabled: boolean;
};

const enabledOption = (step: ZWorkflowStep): PredecessorOption => ({
  step,
  disabled: false,
});

/**
 * Шаги, которые можно выбрать в предшественниках шага step:
 * - у Initial предшественников нет;
 * - у Success и Failure - только пользовательские шаги;
 * - у пользовательского шага - Initial первым, затем другие пользовательские шаги.
 *   Initial неактивен, если уже выбран у другого шага.
 */
export const getPredecessorOptions = (
  step: ZWorkflowStep,
  steps: ZWorkflowStep[],
): PredecessorOption[] => {
  if (isInitialStep(step)) return [];
  const userSteps = steps.filter(isUserStep);
  if (isSystemStep(step)) return userSteps.map(enabledOption);
  const options = userSteps
    .filter(({ id }) => id !== step.id)
    .map(enabledOption);
  const initial = findInitialStep(steps);
  if (!initial) return options;
  const takenByOther = userSteps.some(
    (other) => other.id !== step.id && hasPredecessor(other, initial.id),
  );
  // Если Initial уже выбран у этого шага, его можно снять
  const disabled = takenByOther && !hasPredecessor(step, initial.id);
  return [{ step: initial, disabled }, ...options];
};

/**
 * Шаги, на которые можно перевести рабочую версию с шага currentStepId.
 * Initial пользователю не показывается.
 * Failure скрыт до MDM2-1158: сейчас бэкенд при переходе на него утверждает РВ, как при Success.
 */
export const getNextSteps = (
  steps: ZWorkflowStep[],
  currentStepId: string,
): ZWorkflowStep[] =>
  steps
    .filter((step) => hasPredecessor(step, currentStepId))
    .filter(
      (step) => isUserStep(step) || step.name === zSystemStepName.enum.SUCCESS,
    );

/**
 * Ссылка на шаг для предшественников.
 * Бэкенд связывает предшественников по системному названию (label), а не по id.
 */
export const toStepRef = ({ id, name }: ZWorkflowStep): ZSidLabel => ({
  id,
  label: name,
});

/**
 * В форме label предшественника - отображаемое название шага.
 * Перед сохранением его нужно заменить на системное.
 */
export const withPredecessorNames = (
  steps: ZWorkflowStep[],
): ZWorkflowStep[] => {
  const stepById = new Map(steps.map((step) => [step.id, step]));
  return steps.map((step) => ({
    ...step,
    predecessors: step.predecessors?.map((ref) => {
      const target = stepById.get(ref.id);
      return target ? toStepRef(target) : ref;
    }),
  }));
};

/**
 * Бэкенд сохраняет РП с пользовательскими шагами, только если Initial указан
 * в предшественниках ровно у одного из них. Проверка нужна для понятного текста ошибки.
 * @returns текст ошибки или undefined
 */
export const checkInitialPredecessor = (
  steps: ZWorkflowStep[],
): string | undefined => {
  const initial = findInitialStep(steps);
  // Для старого РП без системных шагов проверку выполнит бэкенд
  if (!initial) return undefined;
  const userSteps = steps.filter(isUserStep);
  // РП без пользовательских шагов бэкенд сохраняет, например только что созданный
  if (!userSteps.length) return undefined;
  const stepsWithInitial = userSteps.filter((step) =>
    hasPredecessor(step, initial.id),
  );
  const [first] = stepsWithInitial;
  if (!first) {
    return "Укажите системный шаг Initial в предшественниках одного пользовательского шага";
  }
  if (stepsWithInitial.length > 1) {
    // Реестр ошибок, п. 6.9
    return `Системный шаг Initial уже указан в предшественниках шага «${getWorkflowStepDisplayName(first)}». Initial можно указать только у одного пользовательского шага рабочего потока.`;
  }
  return undefined;
};
